import { ZODIAC_GLYPHS, PLANET_GLYPHS } from "../lib/api";

/**
 * Natal wheel — Ascendant at 9 o'clock (left), houses 1→12 go counter-clockwise,
 * meaning House 1 sits below the AC and House 12 sits above.
 *
 * Math: SVG angle increases clockwise (since y is inverted). To make longitude
 * advance counter-clockwise on screen starting from Asc=left, we use:
 *    angle = 180 - (lon - ascLon)
 */
export default function ChartWheel({ chart, size = 520 }) {
  if (!chart) return null;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size * 0.46;
  const rZodiac = size * 0.40;
  const rHouse = size * 0.30;
  const rPlanet = size * 0.36;

  const ascLon = chart.ascendant.longitude;
  const toAngle = (lon) => ((180 - (lon - ascLon)) % 360 + 360) % 360;
  const polar = (r, deg) => {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };

  return (
    <div className="relative w-full mx-auto" style={{ maxWidth: size }}>
      <div className="absolute inset-0 starfield starfield-drift pointer-events-none rounded-full" />
      <svg width="100%" style={{ maxWidth: size }} viewBox={`0 0 ${size} ${size}`} data-testid="chart-wheel" className="block h-auto mx-auto">
        <circle cx={cx} cy={cy} r={rOuter} fill="none" className="wheel-line-strong" />
        <circle cx={cx} cy={cy} r={rZodiac} fill="none" className="wheel-line" />
        <circle cx={cx} cy={cy} r={rHouse} fill="none" className="wheel-line" />

        {/* Zodiac divisions every 30° */}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = toAngle(i * 30);
          const [x1, y1] = polar(rHouse, a);
          const [x2, y2] = polar(rOuter, a);
          return <line key={`z-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} className="wheel-line" />;
        })}

        {/* Sign glyphs */}
        {Object.keys(ZODIAC_GLYPHS).map((sign, i) => {
          const a = toAngle(i * 30 + 15);
          const [x, y] = polar((rOuter + rZodiac) / 2, a);
          return (
            <text key={sign} x={x} y={y + 6} textAnchor="middle" className="wheel-glyph">
              {ZODIAC_GLYPHS[sign]}
            </text>
          );
        })}

        {/* House cusps */}
        {chart.houses.map((h, i) => {
          const a = toAngle(h.longitude);
          const [x1, y1] = polar(0, a);
          const [x2, y2] = polar(rHouse, a);
          const isAngular = [0, 3, 6, 9].includes(i);
          return (
            <line
              key={`h-${i}`}
              x1={x1} y1={y1} x2={x2} y2={y2}
              className={isAngular ? "wheel-line-strong" : "wheel-line"}
            />
          );
        })}

        {/* House numbers (in middle of each house, advancing counter-clockwise) */}
        {chart.houses.map((h, i) => {
          const next = chart.houses[(i + 1) % 12].longitude;
          let span = (next - h.longitude + 360) % 360;
          if (span === 0) span = 30;
          const mid = (h.longitude + span / 2) % 360;
          const [x, y] = polar(rHouse * 0.62, toAngle(mid));
          return (
            <text key={`hn-${i}`} x={x} y={y + 4} textAnchor="middle" className="wheel-text" fill="#A1A1AA">
              {i + 1}
            </text>
          );
        })}

        {/* Planets */}
        {chart.planets.map((p) => {
          const a = toAngle(p.longitude);
          const [x, y] = polar(rPlanet, a);
          return (
            <g key={p.planet}>
              <circle cx={x} cy={y} r="14" fill="#0C0C16" stroke="#C9A25E" strokeOpacity="0.55" />
              <text x={x} y={y + 5} textAnchor="middle" className="wheel-glyph" style={{fontSize: 16}}>
                {PLANET_GLYPHS[p.planet]}
              </text>
            </g>
          );
        })}

        {/* Cardinal axis markers */}
        <text x={4} y={cy + 4} className="wheel-text" fill="#C9A25E" fontWeight="600">AC</text>
        <text x={size - 24} y={cy + 4} className="wheel-text" fill="#71717A">DC</text>
        <text x={cx - 8} y={14} className="wheel-text" fill="#71717A">MC</text>
        <text x={cx - 6} y={size - 4} className="wheel-text" fill="#71717A">IC</text>
      </svg>
    </div>
  );
}
